import {
  useState, useEffect 
} from 'react';
import {
  X,
  Gavel,
  FileText,
  Calendar,
  MapPin,
  DollarSign,
  Clock,
  Users,
  TrendingDown,
  Package,
  Building2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  type SourcingEvent,
  type Bid,
  SOURCING_STATUS_LABELS,
  SOURCING_STATUS_COLORS,
  EVENT_TYPE_LABELS,
  EVENT_TYPE_COLORS,
} from '@/features/sourcing/types';

interface EventDetailModalProps {
  readonly event: SourcingEvent | null;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly bids?: Bid[];
}

export function EventDetailModal({
  event,
  isOpen,
  onClose,
  bids = [],
}: EventDetailModalProps) {
  const [timeRemaining, setTimeRemaining] = useState('');

  useEffect(() => {
    if (event?.eventType !== 'reverse_auction') return;

    const updateTimer = () => {
      const now = Date.now();
      const endTime = event.auctionEndTime ? new Date(event.auctionEndTime).getTime() : 0;
      const startTime = event.auctionStartTime ? new Date(event.auctionStartTime).getTime() : 0;

      if (now < startTime) {
        const diff = startTime - now;
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        setTimeRemaining(`Starts in ${hours}h ${mins}m`);
      } else if (now < endTime) {
        const diff = endTime - now;
        const mins = Math.floor(diff / (1000 * 60));
        const secs = Math.floor((diff % (1000 * 60)) / 1000);
        setTimeRemaining(`${mins}:${secs.toString().padStart(2, '0')} remaining`);
      } else {
        setTimeRemaining('Ended');
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [event]);

  if (!isOpen || !event) return null;

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: event.currency,
    }).format(amount);

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

  const formatDateTime = (dateStr: string) =>
    new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  const isReverseAuction = event.eventType === 'reverse_auction';
  const now = Date.now();
  const auctionStart = event.auctionStartTime ? new Date(event.auctionStartTime).getTime() : 0;
  const auctionEnd = event.auctionEndTime ? new Date(event.auctionEndTime).getTime() : 0;
  const isLive = isReverseAuction && now >= auctionStart && now < auctionEnd;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <button
        type="button"
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-label="Close modal"
      />
      <div className="relative z-10 w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-background rounded-lg shadow-xl m-4">
        <div className="sticky top-0 bg-background border-b px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${isReverseAuction ? 'bg-orange-100' : 'bg-indigo-100'}`}>
              {isReverseAuction ? (
                <Gavel className="h-5 w-5 text-orange-600" />
              ) : (
                <FileText className="h-5 w-5 text-indigo-600" />
              )}
            </div>
            <div>
              <h2 className="text-xl font-semibold">{event.title}</h2>
              <p className="text-sm text-muted-foreground">{event.eventNumber}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${EVENT_TYPE_COLORS[event.eventType]}`}>
              {EVENT_TYPE_LABELS[event.eventType]}
            </span>
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${SOURCING_STATUS_COLORS[event.status]}`}>
              {SOURCING_STATUS_LABELS[event.status]}
            </span>
            {isLive && (
              <span className="relative flex h-3 w-3 ml-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-orange-500" />
              </span>
            )}
            <Button variant="ghost" size="icon" onClick={onClose} className="ml-2">
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {isReverseAuction && (
            <Card className={isLive ? 'ring-2 ring-orange-500' : ''}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingDown className="h-4 w-4" />
                  Auction Status
                  {isLive && (
                    <span className="text-orange-600 text-sm font-normal ml-2">
                      {timeRemaining}
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Starting Price</p>
                    <p className="font-semibold">{formatCurrency(event.startingPrice ?? 0)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Current Lowest</p>
                    <p className="font-semibold text-green-600">
                      {event.currentLowestBid ? formatCurrency(event.currentLowestBid) : 'No bids yet'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Total Bids</p>
                    <p className="font-semibold">{event.totalBids ?? 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Reserve Price</p>
                    <p className="font-semibold">{event.reservePrice ? formatCurrency(event.reservePrice) : 'Not set'}</p>
                  </div>
                </div>
                {event.auctionStartTime && event.auctionEndTime && (
                  <div className="mt-4 pt-4 border-t text-sm text-muted-foreground">
                    <Clock className="h-4 w-4 inline mr-1" />
                    Auction window: {formatDateTime(event.auctionStartTime)} - {formatDateTime(event.auctionEndTime)}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Event Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Delivery Zone</p>
                    <p className="text-sm text-muted-foreground">{event.deliveryZone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Building2 className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Delivery Site</p>
                    <p className="text-sm text-muted-foreground">{event.deliverySiteName}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Required Delivery Date</p>
                    <p className="text-sm text-muted-foreground">{formatDate(event.requiredDeliveryDate)}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Bid Deadline</p>
                    <p className="text-sm text-muted-foreground">{formatDateTime(event.bidDeadline)}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <DollarSign className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Estimated Value</p>
                    <p className="text-sm text-muted-foreground">{formatCurrency(event.estimatedValue)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Event Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {event.description && (
                  <div>
                    <p className="text-sm font-medium mb-1">Description</p>
                    <p className="text-sm text-muted-foreground">{event.description}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm font-medium mb-1">Created By</p>
                  <p className="text-sm text-muted-foreground">{event.createdByName}</p>
                </div>
                <div>
                  <p className="text-sm font-medium mb-1">Created At</p>
                  <p className="text-sm text-muted-foreground">{formatDateTime(event.createdAt)}</p>
                </div>
                <div>
                  <p className="text-sm font-medium mb-1">Last Updated</p>
                  <p className="text-sm text-muted-foreground">{formatDateTime(event.updatedAt)}</p>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Package className="h-4 w-4" />
                Line Items ({event.lines.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 font-medium">Item</th>
                      <th className="text-left py-2 font-medium">SKU</th>
                      <th className="text-left py-2 font-medium">Category</th>
                      <th className="text-right py-2 font-medium">Quantity</th>
                      <th className="text-left py-2 font-medium">Unit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {event.lines.map((line) => (
                      <tr key={line.lineId} className="border-b last:border-0">
                        <td className="py-2">{line.itemName}</td>
                        <td className="py-2 text-muted-foreground">{line.sku}</td>
                        <td className="py-2">
                          <span className="rounded bg-muted px-2 py-0.5 text-xs">{line.category}</span>
                        </td>
                        <td className="py-2 text-right">{line.quantity}</td>
                        <td className="py-2 text-muted-foreground">{line.unit}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {bids.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Bids ({bids.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {bids.map((bid, index) => (
                    <div
                      key={bid.bidId}
                      className={`flex items-center justify-between p-3 rounded-lg ${
                        index === 0 ? 'bg-green-50 border border-green-200' : 'bg-muted/50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                          index === 0 ? 'bg-green-600 text-white' : 'bg-muted-foreground/20'
                        }`}>
                          {index + 1}
                        </span>
                        <span className="font-medium">{bid.supplierName}</span>
                      </div>
                      <span className={`font-semibold ${index === 0 ? 'text-green-600' : ''}`}>
                        {formatCurrency(bid.total)}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="sticky bottom-0 bg-background border-t px-6 py-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          {event.status === 'evaluation' && (
            <Button>Review Bids</Button>
          )}
          {event.status === 'open' && (
            <Button variant="destructive">Close Event</Button>
          )}
        </div>
      </div>
    </div>
  );
}
